import React from "react";
import { apiBaseUrl } from "../../../commonUtils/commonUtils.js";
import { Col, Form, Input, Typography, Alert } from "antd";
import { validatePrimaryPhone } from "../../../utils/validators";
import { ReactComponent as Alerticon } from "../../../assets/icons/alert.svg";

const { Text } = Typography;

export default function BusinessPhoneField({
  onStatusChange,
  minDigits = 10,
  debounceMs = 400,
}) {
  const form = Form.useFormInstance();
  const [checking, setChecking] = React.useState(false);
  const [flags, setFlags] = React.useState(null);
  const [apiStatus, setApiStatus] = React.useState(null);
  // eslint-disable-next-line
  const [lastPhone, setLastPhone] = React.useState("");

  const abortRef = React.useRef(null);
  const timerRef = React.useRef(null);

  const runCheck = React.useCallback(
    async (digits) => {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      try {
        setChecking(true);
        const resp = await fetch(
          `${apiBaseUrl}/accounts/account-checker/?phone_number=${encodeURIComponent(
            digits
          )}`,
          {
            signal: abortRef.current.signal,
          }
        );
        const json = await resp.json();
        const data = json?.data || null;
        const next = data
          ? {
              lineOfCreditEnabled: !!data.lineOfCreditEnabled,
              mobCreditEnabled: !!data.mobCreditEnabled,
            }
          : null;
        setApiStatus(json?.status === false ? false : true);
        setLastPhone(digits);
        setFlags(next);
      } catch {
        setApiStatus(null);
        setFlags(null);
      } finally {
        setChecking(false);
        if (form) form.validateFields(["business_phone"]);
      }
    },
    [form]
  );

  const onChange = React.useCallback(
    (value) => {
      const digits = (value || "").replace(/\D/g, "");
      if (timerRef.current) clearTimeout(timerRef.current);
      if (digits.length >= minDigits) {
        timerRef.current = setTimeout(() => runCheck(digits), debounceMs);
      } else {
        setApiStatus(null);
        setFlags(null);
      }
    },
    [debounceMs, minDigits, runCheck]
  );

  const onBlur = React.useCallback(
    (value) => {
      const digits = (value || "").replace(/\D/g, "");
      if (digits.length >= minDigits) runCheck(digits);
    },
    [minDigits, runCheck]
  );

  React.useEffect(() => {
    if (onStatusChange)
      onStatusChange({ ...flags, accountAllowed: apiStatus !== false });
  }, [flags, apiStatus, onStatusChange]);

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const showAnyBanner =
    checking || flags?.lineOfCreditEnabled || flags?.mobCreditEnabled;

  return (
    <>
      <Col xs={24} md={12}>
        <Form.Item
          label={
            <>
              Business mobile (for OTP)
              <Text className="text-danger ml-2"> *</Text>
            </>
          }
          name="business_phone"
          rules={[
            { required: true, message: "Enter business mobile" },
            {
              validator: async (_, v) => {
                const digits = (v || "").replace(/\D/g, "");
                if (!validatePrimaryPhone(digits)) {
                  throw new Error(
                    "Enter a valid 10-digit mobile number (starts 6–9)"
                  );
                }
              },
            },
          ]}
          //extra={<Text type="secondary">Used to verify the applicant via OTP.</Text>}
        >
          <Input
            maxLength={12}
            placeholder="Enter business mobile"
            className="pan-input"
            onChange={(e) => onChange(e.target.value)}
            onBlur={(e) => onBlur(e.target.value)}
          />
        </Form.Item>
      </Col>

      {showAnyBanner && (
        <Col span={24}>
          {flags?.lineOfCreditEnabled && (
            <Col className="detail-notify">
              <Alerticon />
              <Text style={{fontWeight:"normal"}}>
              This mobile number is already linked to an active Direct
              mobCREDIT. Please deactivate the existing account or use a
              different number to proceed.
              </Text>
            </Col>
          )}
          {flags?.mobCreditEnabled && (
            <Alert
              message={
                <span>
                  <strong>Note:</strong> This mobile number is already linked to
                  an active mobCREDIT.
                </span>
              }
              type="warning"
              showIcon={false}
              style={{
                background: "#fffbe6",
                border: "none",
                borderRadius: 8,
                padding: "10px 16px",
                fontSize: 14,
                marginTop: 8,
              }}
            />
          )}
        </Col>
      )}
    </>
  );
}
